import {
  CLOSE_CONFIRM_MODAL,
  MODAL_CONTEXT,
  OPEN_CONFIRM_MODAL,
} from "constants/actionTypes";
import { ModalTypes } from "constants/navigation";
import { Action } from "redux";

/**
 * A modal action
 */
export interface ModalAction {
  // an explicit data-tour attribute for this button, defaults to the label value if not specified
  dataTour?: string;

  // the button label for this action
  label: string;

  // callback event handler when the button is clicked, will be prioritized over onClickAction when both as specified
  onClick?: () => void;

  // callback event handler that executes the returned handler when the button is clicked
  onClickAction?: () => Action;

  // indicates whether the button should take on the 'secondary' button styling
  secondary?: boolean;
}

/**
 * Properties used to configure the look and behaviour of the Confirm Modal
 */
export interface ModalProps {
  // The text to display in the modal title
  title: string;

  // message to display in the modal body
  message: string;

  // if set the time to display the modal for.
  // Used in combination with TAKE_FILE and SELL_FILE create actions
  timeOut?: number;

  // Some kind of hack :[
  // Used in combination with TAKE_FILE and SELL_FILE create actions
  nlisSubmitting?: boolean;

  // The actions the user can take in the modal
  // displayed in the modal footer
  actions: ModalAction[];
}

export interface OpenConfirmModalAction {
  type: typeof OPEN_CONFIRM_MODAL;
  modalProps: ModalProps;
}

/**
 * Returns a fully configured Open Confirm Modal action
 */
export function openConfirmModal(
  modalProps: ModalProps,
): OpenConfirmModalAction {
  return {
    type: OPEN_CONFIRM_MODAL,
    modalProps,
  };
}

export interface CloseConfirmModalAction {
  type: typeof CLOSE_CONFIRM_MODAL;
}

export function closeConfirmModal(): CloseConfirmModalAction {
  return {
    type: CLOSE_CONFIRM_MODAL,
  };
}

export interface UpdateModalContextAction {
  type: typeof MODAL_CONTEXT.UPDATE.ACTION;
  modalType: ModalTypes;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Modal context varies by modal type
  context: any;
}

export function updateModalContext(
  modalType: ModalTypes,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Modal context varies by modal type
  context: any,
): UpdateModalContextAction {
  return {
    type: MODAL_CONTEXT.UPDATE.ACTION,
    modalType,
    context,
  };
}

export interface CloseDispatchingModalAction {
  type: typeof MODAL_CONTEXT.CLOSE.ACTION;
  modalType: ModalTypes;
  navigate: boolean;
}

export function closeDispatchingModal(
  modalType: ModalTypes,
  navigate = true,
): CloseDispatchingModalAction {
  return {
    type: MODAL_CONTEXT.CLOSE.ACTION,
    modalType,
    navigate,
  };
}

export interface OpenDispatchingModalAction {
  type: typeof MODAL_CONTEXT.OPEN.ACTION;
  modalType: ModalTypes;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- returnTo can be Location or string
  returnTo: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Modal context varies by modal type
  context: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Dynamic modal properties
  [key: string]: any;
}

export function openDispatchingModal(
  modalType: ModalTypes,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- returnTo can be Location or string
  returnTo: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Modal context varies by modal type
  context: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Dynamic modal properties spread into action
  modalProps: any = null,
): OpenDispatchingModalAction {
  return {
    type: MODAL_CONTEXT.OPEN.ACTION,
    modalType,
    returnTo,
    context,
    ...modalProps,
  };
}

export interface NavigateToDispatchingModalAction {
  type: typeof MODAL_CONTEXT.OPEN.REQUEST;
  modalType: ModalTypes;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Dynamic modal properties vary by modal type
  modalProps: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- returnTo can be Location or string
  returnTo: any;
}

export function navigateToDispatchingModal(
  modalType: ModalTypes,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Dynamic modal properties vary by modal type
  modalProps: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- returnTo can be Location or string
  returnTo: any,
): NavigateToDispatchingModalAction {
  return {
    type: MODAL_CONTEXT.OPEN.REQUEST,
    modalType,
    modalProps,
    returnTo,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Generic default for modal context
export interface SetModalContextAction<ContextType = any> {
  type: typeof MODAL_CONTEXT.SET.ACTION;
  modalType: ModalTypes;
  context: ContextType;
}

export function setModalContext<ContextType>(
  modalType: ModalTypes,
  context: ContextType,
): SetModalContextAction {
  return {
    type: MODAL_CONTEXT.SET.ACTION,
    modalType,
    context,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Generic default for modal result
export interface PublishModalResultAction<ModelResultType = any> {
  type: typeof MODAL_CONTEXT.RESULT.ACTION;
  modalType: ModalTypes;
  result: ModelResultType | null;
}

export function publishModalResult<ModalResultType>(
  modalType: ModalTypes,
  result: ModalResultType = null,
): PublishModalResultAction<ModalResultType> {
  return {
    type: MODAL_CONTEXT.RESULT.ACTION,
    modalType,
    result,
  };
}
